// Einrichtungsstand aller Kunden (nur Admin).
//
// Antwortet auf „ist dieser Kunde fertig konfiguriert?" aus den DATEN statt aus
// einer Checkliste: Kommen Tracking-Ereignisse an, liefert die Search Console,
// stehen Werbedaten da, wurde je eine Anfrage gestellt. Eine Checkliste sagt,
// was man tun sollte — diese Seite sagt, was tatsächlich funktioniert.

import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { getOnboardingStatus, type StatusKunde, type StatusPruefung } from '@/lib/analytics-api';
import KeywordTracking from '@/components/einrichtung/KeywordTracking';

const artLabel: Record<string, string> = {
  landingpage: 'Landingpage + Ads',
  website: 'Komplette Website',
  bewerbungen: 'Mitarbeitergewinnung',
};

function Ampel({ p }: { p: StatusPruefung }) {
  const stil =
    p.stand === 'ok'
      ? 'bg-green-50 text-green-700 border-green-200'
      : p.stand === 'offen'
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-gray-50 text-gray-400 border-gray-200';
  const zeichen = p.stand === 'ok' ? '✓' : p.stand === 'offen' ? '!' : '–';
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${stil}`}>
      <span className="mt-px font-bold leading-none">{zeichen}</span>
      <div className="min-w-0">
        <p className="text-xs font-semibold leading-tight">{p.was}</p>
        <p className="text-[11px] leading-snug opacity-90 break-words">{p.detail}</p>
      </div>
    </div>
  );
}

export default function EinrichtungPage() {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: getOnboardingStatus,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
      </div>
    );
  }

  const kunden = data?.kunden || [];
  const offen = data?.offen_gesamt ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Einrichtung</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Was bei jedem Kunden tatsächlich ankommt — nicht was auf einer Liste steht. Grün heißt: Daten
            fließen. Gelb heißt: hier fehlt etwas. Grau heißt: bei dieser Projektart nicht vorgesehen.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${
              offen === 0 ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            {offen === 0 ? 'Alles eingerichtet' : `${offen} offene Punkte`}
          </span>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Neu prüfen
          </button>
        </div>
      </div>

      {/* Eine Einstellung, die für alle Werbekonten auf einmal gilt — deshalb
          oben, vor der Liste je Kunde (12.09.2026). */}
      <KeywordTracking />

      {kunden.map((k: StatusKunde) => (
        <div key={k.id} className="rounded-xl border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-semibold text-gray-900">{k.name}</h2>
            {k.software_only && (
              <span className="rounded border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
                Software/Plattform
              </span>
            )}
            <span
              className={`ml-auto text-xs font-medium ${k.offen === 0 ? 'text-green-700' : 'text-amber-700'}`}
            >
              {k.offen === 0 ? 'vollständig' : `${k.offen} offen`}
            </span>
          </div>

          <div className="space-y-4 px-5 py-4">
            {k.hinweis && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {k.hinweis}
              </p>
            )}

            <div className="grid gap-2 sm:grid-cols-3">
              {k.pruefungen.map((p) => (
                <Ampel key={p.was} p={p} />
              ))}
            </div>

            {k.projekte.map((pr) => (
              <div key={pr.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-gray-900">{pr.name}</span>
                  <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-600">
                    {artLabel[pr.art] || pr.art}
                  </span>
                  <code className="text-[11px] text-gray-500">{pr.slug}</code>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {pr.pruefungen.map((p) => (
                    <Ampel key={p.was} p={p} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
